import { DialogContent, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  TextArea,
  Title,
  ToggleButton,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { isSameMinute } from "date-fns";

import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";

interface UpdateEtaBottomSheetProps {
  modalState: UseModalState;
  shiftStartTime: Date;
  workplaceTimezone: string;
  etaUpdateAvailableTimes: Date[];
  selectedEta: Date;
  message: string;
  isSubmitting: boolean;
  onEtaChange: (eta: Date) => void;
  onMessageChange: (message: string) => void;
  onSubmit: () => Promise<void>;
}

export function UpdateEtaBottomSheet(props: UpdateEtaBottomSheetProps) {
  const {
    modalState,
    workplaceTimezone,
    shiftStartTime,
    etaUpdateAvailableTimes,
    selectedEta,
    message,
    onEtaChange,
    onMessageChange,
    onSubmit,
    isSubmitting,
  } = props;

  // eslint-disable-next-line prefer-destructuring
  const firstTime = etaUpdateAvailableTimes[0];
  const isFirstTimeOnTime = firstTime && isSameMinute(firstTime, shiftStartTime);
  const initialMessage = firstTime
    ? `I will be arriving to this shift ${
        isFirstTimeOnTime ? "on time at" : "by"
      } ${formatTimeWithTimezone(firstTime, workplaceTimezone, false)}`
    : "";

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            isLoading={isSubmitting}
            size="large"
            variant="contained"
            endIconType="plane"
            onClick={onSubmit}
          >
            Send message to facility
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ textAlign: "left" }}>
        <Stack direction="column" spacing={6} px={3}>
          <Title variant="h3" component="h3">
            My ETA is:
          </Title>

          <Stack direction="row" flexWrap="wrap" sx={{ gap: 4 }}>
            {etaUpdateAvailableTimes.map((option) => {
              const isShiftStartTime = isSameMinute(option, shiftStartTime);
              const formattedTime = formatTimeWithTimezone(option, workplaceTimezone, false);
              return (
                <ToggleButton
                  key={option.toISOString()}
                  value={option}
                  sx={{ minWidth: "auto", px: 5 }}
                  selected={selectedEta.toISOString() === option.toISOString()}
                  onChange={(_, value: Date) => {
                    if (value !== null) {
                      onEtaChange(value);
                    }
                  }}
                >
                  {`${isShiftStartTime ? "on time, at " : ""}${formattedTime}`}
                </ToggleButton>
              );
            })}
          </Stack>
          <TextArea
            fullWidth
            rows={3}
            placeholder={initialMessage}
            value={message}
            onChange={(event) => {
              onMessageChange(event.target.value);
            }}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
